// Dedicated lead magnet landing page — /free-guide
// Standalone (no site nav/footer): hero, YouTube embed, benefits, download CTA.
// Email is captured upstream via ManyChat — this page only delivers the guide.
import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { BookOpen } from "lucide-react";

const NAVY = "#1B2F5E";
const CREAM = "#F5F0E8";
const GOLD = "#E8952A";
const PDF_URL =
  "https://customer-assets-jai6qajn.emergentagent.net/wingman/" +
  "cb668c5e-4674-4a54-8793-10816f499137/attachments/" +
  "fc2123716df046398fb58f834d776d2d_beat_information_overload.pdf";
const YOUTUBE_ID = "YogZkoAivMY";

const BENEFITS = [
  "Learn why you forget 70% of what you read (and how to fix it)",
  "A simple visual system to retain more from every book",
  "The exact framework to beat information overload starting today",
];

export default function FreeGuidePage() {
  const [downloaded, setDownloaded] = useState(false);

  return (
    <div className="min-h-screen" style={{ background: "#ffffff" }} data-testid="free-guide-page">
      <Helmet>
        <title>Beat Information Overload — Free Guide | Summary Maps</title>
        <meta
          name="description"
          content="Download our free guide and learn how to cut through the noise, retain more from books, and stop forgetting what you read. Free from Summary Maps."
        />
        {/* Canonical link is emitted by the layout-level CanonicalTag (App.js). */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Beat Information Overload — Free Guide | Summary Maps" />
        <meta
          property="og:description"
          content="Download our free guide and learn how to cut through the noise, retain more from books, and stop forgetting what you read. Free from Summary Maps."
        />
        <meta property="og:url" content="https://summarymapsjournal.com/free-guide" />
      </Helmet>

      {/* Hero */}
      <section style={{ background: NAVY }} className="px-4 sm:px-6 pt-6 sm:pt-8 pb-14 sm:pb-20">
        <div className="max-w-3xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2.5" data-testid="free-guide-logo">
            <span
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: "rgba(255,255,255,0.12)", color: "#ffffff" }}
            >
              <BookOpen className="w-4 h-4" />
            </span>
            <span className="leading-tight">
              <span className="block font-serif text-lg font-semibold text-white">
                Summary<span style={{ color: GOLD }}>Maps</span>
              </span>
              <span className="block text-[10px] uppercase tracking-[0.18em] -mt-0.5" style={{ color: CREAM }}>
                Journal
              </span>
            </span>
          </Link>

          <h1
            className="font-serif font-bold text-white mt-12 sm:mt-16 text-4xl sm:text-5xl lg:text-6xl"
            style={{ lineHeight: 1.1 }}
          >
            Stop Drowning in Information
          </h1>
          <p
            className="mt-5 sm:mt-6 text-base md:text-lg max-w-xl"
            style={{ color: CREAM, lineHeight: 1.65 }}
          >
            Get our free guide and learn how to cut through the noise — retain what matters and forget the rest.
          </p>
        </div>
      </section>

      {/* Video */}
      <section className="px-4 sm:px-6 py-12 sm:py-16" style={{ background: "#ffffff" }}>
        <div className="max-w-3xl mx-auto">
          <div
            className="relative w-full overflow-hidden rounded-2xl shadow-lg"
            style={{ paddingTop: "56.25%" /* 16:9 */ }}
            data-testid="free-guide-video"
          >
            <iframe
              src={`https://www.youtube.com/embed/${YOUTUBE_ID}?autoplay=1&loop=1&playlist=${YOUTUBE_ID}&mute=1&controls=0`}
              title="Beat Information Overload — video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
            />
          </div>
          <p
            className="text-center mt-4 text-sm sm:text-base italic"
            style={{ color: GOLD }}
            data-testid="free-guide-video-caption"
          >
            Sound familiar? There's a better way.
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="px-4 sm:px-6 py-12 sm:py-16" style={{ background: CREAM }}>
        <div className="max-w-2xl mx-auto">
          <ul className="space-y-4 sm:space-y-5" data-testid="free-guide-benefits">
            {BENEFITS.map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-base md:text-lg" style={{ color: NAVY }}>
                <span aria-hidden className="flex-shrink-0 mt-0.5">✅</span>
                <span style={{ lineHeight: 1.55 }}>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Download CTA / success */}
      <section className="px-4 sm:px-6 py-14 sm:py-20" style={{ background: "#ffffff" }}>
        <div className="max-w-md mx-auto text-center">
          {downloaded ? (
            <div data-testid="free-guide-success">
              <div style={{ fontSize: 40, lineHeight: 1 }} aria-hidden>🎉</div>
              <h2 className="font-serif font-bold mt-4 text-3xl sm:text-4xl" style={{ color: NAVY }}>
                Your guide is downloading! 🎉
              </h2>
              <a
                href="https://summarymaps.com/?utm_source=journal&utm_medium=lead_magnet&utm_campaign=beat_information_overload"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center mt-8 px-7 py-3.5 rounded-full font-bold text-sm sm:text-base text-white transition-transform hover:scale-105"
                style={{ background: GOLD }}
                data-testid="free-guide-map-cta"
              >
                Start your 7-day free trial →
              </a>
              <p className="mt-4 text-xs sm:text-sm" style={{ color: "#8B7E6A" }}>
                Turn any book into a visual mind map with AI — free trial, no credit card needed.
              </p>
            </div>
          ) : (
            <div data-testid="free-guide-download">
              <h2 className="font-serif font-bold text-3xl sm:text-4xl" style={{ color: NAVY }}>
                Get Your Free Copy
              </h2>
              <a
                href={PDF_URL}
                download="Beat-Information-Overload.pdf"
                onClick={() => setDownloaded(true)}
                className="w-full mt-7 px-6 py-4 rounded-full font-bold text-lg text-white transition-transform hover:scale-[1.02] inline-flex items-center justify-center gap-2"
                style={{ background: GOLD }}
                data-testid="free-guide-download-btn"
              >
                Download Free Guide 📚
              </a>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
